import { useEffect, useRef } from 'react';
import { subscribe } from '@/shared/lib/mqtt/client';
import { useSession } from '@/features/auth/model/use-session';

/**
 * Runs [onMessage] for every message on [topic], for as long as the component is mounted.
 *
 * ⚠️ The handler is held in a ref rather than listed as a dependency. A caller almost always passes
 * an inline arrow, which is a new function on every render — as a dependency that would resubscribe
 * on every render, and a subscribe/unsubscribe storm against the broker is how a control room's live
 * feed becomes the thing that takes the broker down.
 */
export function useTopic(topic: string, onMessage: (payload: unknown) => void): void {
  const handler = useRef(onMessage);
  handler.current = onMessage;

  // ⚠️ Resubscribes when the access token changes, and that is what keeps the feed alive.
  //
  // The broker authenticates with the token (§8.2) and disconnects the client when it expires —
  // fifteen minutes (§12.1). The client reconnects underneath with the renewed one; this is what
  // re-establishes the subscription on top of it. Without the dependency the connection would come
  // back subscribed to nothing, and the panel would sit silent while looking connected.
  const { session } = useSession();
  const token = session?.accessToken;

  useEffect(() => subscribe(topic, (payload) => handler.current(payload)), [topic, token]);
}
